import { useEffect, useState } from 'react'

import { clsx } from 'clsx'
import { useNavigate } from 'react-router'

import { Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useI18n } from '../i18n/providers/I18nProvider.js'
import { SpaceImage } from '../Space/SpaceImage.js'

import { PostOptions } from './PostOptions.js'
import type { PostContext } from './types.js'

/**
 * react-router stores the app-local history index in `history.state.idx`: direct
 * loads start at 0, app pushes increment it, and replace navigations leave it
 * unchanged. That makes `idx > 0` the reliable signal that the previous entry is
 * in-app, even after the canonical post URL replace in `Post.tsx`.
 *
 * `history.length` counts every entry in the tab, including other sites, so a
 * direct link opened in an existing tab can still report a previous entry.
 */
const hasAppHistoryEntry = (): boolean => {
  const idx = (window?.history?.state as { idx?: unknown } | null)?.idx

  return typeof idx === 'number' && idx > 0
}

export const PostHeader = ({
  post,
  context,
  className,
  showBackButton = true,
}: {
  post: Post
  context: PostContext
  className?: string
  showBackButton?: boolean
}) => {
  const { $t } = useI18n()
  const { Link } = useRouter()
  const navigate = useNavigate()

  const [hasBackLink, setHasBackLink] = useState(false)
  useEffect(() => {
    setHasBackLink(hasAppHistoryEntry())
  }, [])

  const onBack = () => {
    if (hasBackLink) {
      navigate(-1)
    }
  }

  const showMoreOptions = post?.status === PostStatus.PUBLISHED

  return (
    <div className={clsx('flex', className)}>
      {showBackButton && (
        <IconButton
          as={hasBackLink ? undefined : Link}
          variant="tertiaryNeutral"
          rounded
          size="lg"
          icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
          href={hasBackLink ? undefined : post?.space?.relativeUrl}
          onClick={onBack}
          aria-label={$t({
            defaultMessage: 'Back',
            id: 'Generics.Back',
          })}
        />
      )}
      <div className="flex-1 min-w-0">
        <Button
          as={Link}
          variant="tertiaryNeutral"
          size="lg"
          className={clsx(
            'rounded-full',
            !post?.space && 'animate-pulse',
            'max-w-full truncate inline-flex',
          )}
          leadingIcon={<SpaceImage space={post?.space} size="lg" as="span" />}
          href={post?.space?.relativeUrl}
          aria-label={post?.space?.name}
          truncate
        >
          {post?.space ? (
            post?.space?.name
          ) : (
            <span className="h-5 w-32 bg-surface-pressed rounded-full"></span>
          )}
        </Button>
      </div>
      {showMoreOptions && (
        <PostOptions post={post} context={context} size="lg" />
      )}
    </div>
  )
}
